import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import {
  AlertCircle,
  AlertTriangle,
  CalendarCheck,
  CheckCircle2,
  ExternalLink,
  Loader2,
  LogIn,
  MapPin,
  ChevronDown,
  CreditCard,
  Plus,
} from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateAddress } from "@/hooks/use-addresses";
import { useDistricts, useGovernorates } from "@/hooks/use-locations";
import type { AssistantMessage } from "@/hooks/use-assistant";

const money = (value: number | undefined, currency: string) =>
  `${Number(value || 0).toFixed(3)} ${currency}`;

/**
 * Plain text with **bold** only. Rendered as React nodes, never as HTML, so
 * nothing the model writes can inject markup.
 */
function RichText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}

interface CardActions {
  onNavigate: (path: string) => void;
  onConfirmDraft: (draftId: string) => void;
  onCancelDraft: (draftId: string) => void;
  onSendMessage: (text: string) => void;
  currentPath: string;
  /** False once the customer has written after this card (older cards collapse). */
  active: boolean;
}

export function AssistantMessageView({ message, ...actions }: { message: AssistantMessage } & CardActions) {
  const { t, i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");
  const name = (m: Record<string, any>): string =>
    (isAr ? m.nameAr || m.serviceNameAr : m.nameEn || m.serviceNameEn) || m.nameEn || m.serviceNameEn || "";

  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div dir="auto" className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-ee-md bg-primary px-3.5 py-2 text-sm text-primary-foreground">
          {message.text}
        </div>
      </div>
    );
  }

  if (message.type === "error") {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{message.text}</span>
      </div>
    );
  }

  if (message.type === "text") {
    return (
      <div className="flex justify-start">
        <div dir="auto" className="max-w-[90%] whitespace-pre-wrap break-words rounded-2xl rounded-es-md bg-muted px-3.5 py-2 text-sm leading-relaxed text-foreground">
          <RichText text={message.text || ""} />
        </div>
      </div>
    );
  }

  if (message.type === "order_draft") return <OrderDraftCard draft={message} {...actions} />;

  if (message.type === "address_form") return <AddressFormCard form={message} {...actions} />;

  const cardClass = "rounded-2xl border bg-card p-3 text-sm shadow-sm";

  if (message.type === "order_placed") {
    return (
      <div className={`${cardClass} border-green-500/30 bg-green-500/5`}>
        <div className="flex items-center gap-2 font-semibold text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-5 w-5" />
          {t("assistant.cards.placed", { number: message.orderNumber })}
        </div>
        <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => actions.onNavigate(`/orders/${message.orderId}`)}>
          {t("assistant.cards.viewOrder")}
        </Button>
      </div>
    );
  }

  if (message.type === "order_link") {
    return (
      <Button variant="outline" size="sm" className="w-full justify-between" onClick={() => actions.onNavigate(`/orders/${message.orderId}`)}>
        <span>{t("assistant.cards.viewOrderNumber", { number: message.orderNumber })}</span>
        <ExternalLink className="h-4 w-4" />
      </Button>
    );
  }

  if (message.type === "booking_link") {
    const params = new URLSearchParams({ serviceId: message.serviceId });
    if (message.vehicleType) params.set("vehicle", message.vehicleType);
    if (message.date) params.set("date", message.date);
    return (
      <div className={cardClass}>
        <p className="font-semibold">{name(message)}</p>
        {message.vehicleType && <p className="text-xs text-muted-foreground">{t(`booking.${message.vehicleType}`)}</p>}
        <Button size="sm" className="mt-2 w-full" onClick={() => actions.onNavigate(`/booking?${params.toString()}`)}>
          <CalendarCheck className="h-4 w-4" />
          {t("assistant.cards.book")}
        </Button>
      </div>
    );
  }

  if (message.type === "whatsapp") {
    const href = `https://wa.me/${message.phone}?text=${encodeURIComponent(message.text || "")}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20BD5A]"
      >
        <SiWhatsapp className="h-5 w-5" />
        {t("assistant.cards.whatsapp")}
      </a>
    );
  }

  if (message.type === "login") {
    return (
      <div className={cardClass}>
        <p className="text-muted-foreground">{t("assistant.cards.loginHint")}</p>
        <Button size="sm" className="mt-2 w-full" onClick={() => actions.onNavigate(`/login?redirect=${encodeURIComponent(actions.currentPath)}`)}>
          <LogIn className="h-4 w-4" />
          {t("assistant.cards.login")}
        </Button>
      </div>
    );
  }

  return null;
}

function OrderDraftCard({ draft, onConfirmDraft, onCancelDraft, onNavigate }: { draft: AssistantMessage } & CardActions) {
  const { t, i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");
  const locale = isAr ? ar : enUS;
  const currency = t("services.currency");
  const label = (m: { nameEn?: string; nameAr?: string }) => (isAr ? m.nameAr : m.nameEn) || m.nameEn || "";

  const expired = draft.status === "pending" && draft.expiresAt && Date.parse(draft.expiresAt) < Date.now();
  const status: string = expired ? "expired" : draft.status || "pending";
  const pending = status === "pending";
  const payLabel: Record<string, string> = {
    knet: t("booking.payment.knet"),
    credit_card: t("booking.payment.creditCard"),
    apple_pay: t("booking.payment.applePay"),
    wallet: t("nav.wallet"),
  };
  const start = draft.appointmentStart ? new Date(draft.appointmentStart) : null;
  const end = draft.appointmentEnd ? new Date(draft.appointmentEnd) : null;

  const Row = ({ name, value, strong }: { name: string; value: string; strong?: boolean }) => (
    <div className={`flex justify-between gap-3 ${strong ? "text-base font-bold text-foreground" : "text-muted-foreground"}`}>
      <span>{name}</span>
      <span className="tabular-nums" dir="ltr">{value}</span>
    </div>
  );

  return (
    <div className={`rounded-2xl border bg-card text-sm shadow-sm ${pending ? "border-primary/40" : "opacity-90"}`} data-testid="assistant-order-card">
      <div className="flex items-center gap-2 border-b px-3 py-2.5 font-semibold">
        <CalendarCheck className="h-4 w-4 text-primary" />
        {t("assistant.order.title")}
      </div>

      <div className="space-y-2 px-3 py-2.5">
        {(draft.items || []).map((item: any, i: number) => (
          <div key={i}>
            <div className="flex justify-between gap-3">
              <span className="font-medium">
                {label(item)}
                <span className="text-muted-foreground">
                  {" · "}
                  {t(`booking.${item.vehicleType}`)}
                  {item.feet ? ` · ${t("assistant.order.feet", { count: item.feet })}` : ""}
                  {item.quantity > 1 ? ` ×${item.quantity}` : ""}
                </span>
              </span>
              <span className="shrink-0 tabular-nums" dir="ltr">{money(item.subtotal, currency)}</span>
            </div>
            {item.addOns?.length > 0 && (
              <p className="text-xs text-muted-foreground">+ {item.addOns.map(label).join("، ")}</p>
            )}
          </div>
        ))}
        {draft.orderAddOns?.length > 0 && (
          <p className="text-xs text-muted-foreground">+ {draft.orderAddOns.map(label).join("، ")}</p>
        )}

        {start && (
          <div className="flex items-start gap-2 text-muted-foreground">
            <CalendarCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {format(start, "EEEE d MMMM", { locale })} · {format(start, "h:mm a", { locale })}
              {end ? ` – ${format(end, "h:mm a", { locale })}` : ""}
            </span>
          </div>
        )}
        {draft.address && (
          <div className="flex items-start gap-2 text-muted-foreground">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {draft.address.label} — {isAr ? draft.address.areaAr : draft.address.areaEn}
            </span>
          </div>
        )}
        <div className="flex items-start gap-2 text-muted-foreground">
          <CreditCard className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{payLabel[draft.paymentMethod] || draft.paymentMethod}</span>
        </div>
      </div>

      <div className="space-y-1 border-t px-3 py-2.5">
        <Row name={t("assistant.order.subtotal")} value={money(draft.subtotal, currency)} />
        <Row name={t("booking.deliveryFee")} value={money(draft.deliveryFee, currency)} />
        {draft.discountAmount > 0 && (
          <Row
            name={`${t("assistant.order.discount")}${draft.couponCode ? ` (${draft.couponCode})` : ""}`}
            value={`−${money(draft.discountAmount, currency)}`}
          />
        )}
        {draft.walletCreditUsed > 0 && (
          <Row name={t("assistant.order.fromWallet")} value={`−${money(draft.walletCreditUsed, currency)}`} />
        )}
        <Row name={t("assistant.order.due")} value={money(draft.cashAmountDue, currency)} strong />
      </div>

      <div className="space-y-2 border-t px-3 py-2.5">
        {pending && draft.paymentMethod === "apple_pay" && draft.cashAmountDue > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-yellow-500/10 p-2 text-xs text-yellow-700 dark:text-yellow-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t("booking.payment.applePayKnetOnly")}</span>
          </div>
        )}

        {/* Final states explain themselves below; this is for retryable errors. */}
        {draft.error && pending && (
          <div className="flex items-start gap-2 text-xs text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{draft.error}</span>
          </div>
        )}

        {pending ? (
          <>
            <div className="flex gap-2">
              <Button className="flex-1" disabled={draft.busy} onClick={() => onConfirmDraft(draft.draftId)} data-testid="assistant-confirm-order">
                {draft.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {draft.cashAmountDue > 0 ? t("assistant.order.confirmPay") : t("assistant.order.confirm")}
              </Button>
              <Button variant="outline" disabled={draft.busy} onClick={() => onCancelDraft(draft.draftId)}>
                {t("assistant.order.cancel")}
              </Button>
            </div>
            <p className="text-center text-xs text-muted-foreground">{t("assistant.order.notPlacedYet")}</p>
          </>
        ) : status === "confirmed" ? (
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 font-semibold text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              {t("assistant.order.confirmed")}
            </span>
            {draft.orderId && (
              <Button variant="outline" size="sm" onClick={() => onNavigate(`/orders/${draft.orderId}`)}>
                {t("assistant.cards.viewOrder")}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {status === "cancelled"
              ? t("assistant.order.cancelled")
              : status === "stale"
                ? t("assistant.order.stale")
                : status === "failed"
                  ? t("assistant.order.failed")
                  : t("assistant.order.expired")}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * A new saved address, filled in inside the chat. It posts to the same
 * POST /addresses as the Addresses page; nothing is saved until the
 * customer presses Save. Afterwards it tells the assistant, which then
 * reads the addresses again and carries on with the booking.
 */
function AddressFormCard({ form, onSendMessage, active }: { form: AssistantMessage } & CardActions) {
  const { t, i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");
  const [open, setOpen] = useState(active);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState({
    label: form.label || "",
    governorateId: form.governorateId || "",
    districtId: form.districtId || "",
    block: "",
    street: "",
    avenue: "",
    building: "",
    landmark: "",
  });
  const { data: governorates } = useGovernorates();
  const { data: districts } = useDistricts(values.governorateId || undefined);
  const createAddress = useCreateAddress();

  useEffect(() => {
    if (!active) setOpen(false);
  }, [active]);

  const set = (key: keyof typeof values) => (value: string) =>
    setValues((v) => ({ ...v, [key]: value, ...(key === "governorateId" ? { districtId: "" } : {}) }));
  const nameOf = (item?: { nameAr: string; nameEn: string }) => (item ? (isAr ? item.nameAr : item.nameEn) : "");
  const ready = values.governorateId && values.districtId && values.block.trim() && values.street.trim() && values.building.trim();

  const save = async () => {
    if (!ready || createAddress.isPending) return;
    setError(null);
    const label = values.label.trim() || t("addresses.defaultLabel");
    try {
      await createAddress.mutateAsync({
        label,
        governorateId: values.governorateId,
        districtId: values.districtId,
        block: values.block.trim(),
        street: values.street.trim(),
        building: values.building.trim(),
        ...(values.avenue.trim() ? { avenue: values.avenue.trim() } : {}),
        ...(values.landmark.trim() ? { landmark: values.landmark.trim() } : {}),
      });
      const area = nameOf(districts?.find((d) => d.id === values.districtId));
      const summary = `${label} — ${area}`;
      setSaved(summary);
      onSendMessage(t("assistant.address.savedMessage", { address: summary }));
    } catch (e: any) {
      setError(e?.message?.includes("Maximum") ? t("addresses.maxReached", { max: 10 }) : t("assistant.address.error"));
    }
  };

  if (saved) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-green-500/30 bg-green-500/5 p-3 text-sm font-semibold text-green-700 dark:text-green-400">
        <CheckCircle2 className="h-5 w-5 shrink-0" />
        {t("assistant.address.saved", { address: saved })}
      </div>
    );
  }

  if (!open) {
    return (
      <Button variant="outline" size="sm" className="w-full justify-center" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" />
        {t("assistant.address.open")}
      </Button>
    );
  }

  const selectClass =
    "h-10 w-full appearance-none rounded-lg border border-input bg-background ps-3 pe-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
  const arrow = <ChevronDown className="pointer-events-none absolute end-2.5 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />;
  const field = (key: "block" | "street" | "building" | "avenue" | "landmark", required = false) => (
    <label className="space-y-1">
      <span className="text-xs text-muted-foreground">
        {t(`addresses.${key}`)}
        {required ? " *" : ""}
      </span>
      <Input
        value={values[key]}
        onChange={(e) => set(key)(e.target.value)}
        placeholder={t(`addresses.${key}Placeholder`)}
        className="h-10 text-sm"
        data-testid={`assistant-address-${key}`}
      />
    </label>
  );

  return (
    <div className="space-y-3 rounded-2xl border border-primary/40 bg-card p-3 text-sm shadow-sm" data-testid="assistant-address-form">
      <div className="flex items-center gap-2 font-semibold">
        <MapPin className="h-4 w-4 text-primary" />
        {t("assistant.address.title")}
      </div>

      <div className="space-y-1">
        <span className="text-xs text-muted-foreground">{t("addresses.labelName")}</span>
        <div className="flex flex-wrap gap-1.5">
          {(["home", "office", "other"] as const).map((key) => {
            const text = t(`addresses.labels.${key}`);
            return (
              <button
                key={key}
                type="button"
                onClick={() => set("label")(text)}
                className={`rounded-full border px-3 py-1 text-xs ${values.label === text ? "border-primary bg-primary/10 text-primary" : "border-input"}`}
              >
                {text}
              </button>
            );
          })}
        </div>
        <Input
          value={values.label}
          onChange={(e) => set("label")(e.target.value)}
          placeholder={t("addresses.labelPlaceholder")}
          className="h-10 text-sm"
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">{t("assistant.address.governorate")} *</span>
          <div className="relative">
          <select
            className={selectClass}
            value={values.governorateId}
            onChange={(e) => set("governorateId")(e.target.value)}
            data-testid="assistant-address-governorate"
          >
            <option value="" disabled>
              {t("assistant.address.choose")}
            </option>
            {governorates?.map((g) => (
              <option key={g.id} value={g.id}>
                {nameOf(g)}
              </option>
            ))}
          </select>
          {arrow}
          </div>
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">{t("assistant.address.district")} *</span>
          <div className="relative">
          <select
            className={selectClass}
            value={values.districtId}
            onChange={(e) => set("districtId")(e.target.value)}
            disabled={!values.governorateId}
            data-testid="assistant-address-district"
          >
            <option value="" disabled>
              {t("assistant.address.choose")}
            </option>
            {districts?.map((d) => (
              <option key={d.id} value={d.id}>
                {nameOf(d)}
              </option>
            ))}
          </select>
          {arrow}
          </div>
        </label>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {field("block", true)}
        {field("street", true)}
        {field("building", true)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {field("avenue")}
        {field("landmark")}
      </div>

      {error && (
        <div className="flex items-start gap-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Button className="w-full" disabled={!ready || createAddress.isPending} onClick={save} data-testid="assistant-address-save">
        {createAddress.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
        {t("addresses.save")}
      </Button>
      <p className="text-center text-xs text-muted-foreground">{t("assistant.address.hint")}</p>
    </div>
  );
}
